import type { TestLateralization } from '@/components/sop-intake/sop-analyzer/types';

const INVERTED_BIAS_KEYS = new Set<string>([
  'CervicalLateralFlexion',
  'ShoulderIR',
  'FemoralER',
  'HipAbduction',
]);

const RULEDOC_EXPECTED_RLP: Record<string, string> = {
  CervicalAxialRotation: 'Left < Right',
  CervicalLateralFlexion: 'Left > Right',
  ShoulderIR: 'Left > Right',
  ShoulderER: 'Left < Right',
  ShoulderFlexion: 'Right > Left',
  ShoulderAbduction: 'Left < Right',
  PelvicRotation: 'Left < Right',
  StraightLegRaise: 'Left < Right',
  FemoralIR: 'Left < Right',
  FemoralER: 'Left > Right',
  HipAbduction: 'Left > Right',
  HipExtension: 'Left < Right',
  HipAdductionObers: 'Left < Right',
  FootPresentation: 'Right stance commonly appears more supinated in RLP',
  GroundingPerformance: 'Grounding generally matches lateralization side in intact patterns',
};

const RULEDOC_BIOMECH_NOTES: Record<string, string> = {
  CervicalAxialRotation: 'Head/neck rotation asymmetry contributes to frontal/transverse bias read.',
  CervicalLateralFlexion: 'Side-bend asymmetry is interpreted with inverted directionality in RLP mapping.',
  ShoulderIR: 'Higher left IR can reflect right-lateralized upper-body orientation in this model.',
  ShoulderER: 'Lower left ER is expected in baseline right-lateralized mechanics.',
  ShoulderFlexion: 'Sagittal plane thorax freedom; higher right flexion reflects right-lateralized anterior expansion.',
  ShoulderAbduction: 'Thorax expansion/compression pattern can shift abduction side-to-side.',
  PelvicRotation: 'Pelvic rotation asymmetry is a primary lateralization anchor for stance orientation.',
  StraightLegRaise: 'SLR may be invalidated in APT contexts and should follow orientation notes.',
  FemoralIR: 'Femoral IR asymmetry reflects lower-limb rotational strategy.',
  FemoralER: 'Femoral ER uses inverted directionality in this lateralization mapping.',
  HipAbduction: 'Hip abduction uses inverted directionality relative to most tests.',
  HipExtension: 'Hip extension tracks posterior hip extension/IR accessibility (0 = full extension, negative = degrees short).',
  HipAdductionObers: "Ober's positive values represent pass (knee to table), while 0 or negative values reflect extension limitation.",
  FootPresentation: 'Foot presentation helps contextualize stance/swing strategy and mixed rows.',
  GroundingPerformance: 'Grounding score (dynamometer or perceived) indicates lateralization alignment.',
};

const formatValue = (value: number | null): string => (value === null ? 'N/A' : `${value}°`);

const sideWord = (bias: TestLateralization['bias']): string => {
  if (bias === 'Left') return 'left';
  if (bias === 'Right') return 'right';
  return 'equal';
};

export const getRuleDocExpectedRlp = (testKey: string): string =>
  RULEDOC_EXPECTED_RLP[testKey] ?? 'No explicit RuleDoc directional mapping';

export const getRuleDocBiomechNote = (testKey: string): string =>
  RULEDOC_BIOMECH_NOTES[testKey] ?? 'No additional biomechanical note documented.';

export const getValueSummary = (test: TestLateralization): string => {
  const left = formatValue(test.leftVal);
  const right = formatValue(test.rightVal);
  const goal = test.nominal ? `${test.nominal}°` : 'N/A';
  return `${test.testLabel}: L=${left}, R=${right}, goal=${goal}.`;
};

export const getBiasReason = (test: TestLateralization): string => {
  if (test.isInvalidated) {
    return test.invalidationReason ?? 'This test was invalidated by orientation override.';
  }
  if (test.bias === null) {
    return 'Insufficient data to determine side bias.';
  }
  if (test.bias === 'Even') {
    return 'Both sides read equal for this metric.';
  }

  const comparator = INVERTED_BIAS_KEYS.has(test.testKey) ? 'higher' : 'lower';
  const explainedSide = test.bias === 'Right' ? 'left' : test.bias === 'Left' ? 'right' : sideWord(test.bias);
  const expected = getRuleDocExpectedRlp(test.testKey);
  return `Classified ${test.bias} because the ${explainedSide} side is ${comparator} per this test mapping (expected RLP: ${expected}).`;
};

export const getTestExplanationLines = (test: TestLateralization): string[] => {
  const lines: string[] = [];

  // 1. Directional Decision
  if (test.isInvalidated) {
    lines.push(`Decision: Invalidated - ${test.invalidationReason ?? 'Orientation override'}`);
  } else if (test.bias === null) {
    lines.push('Decision: Insufficient data to determine side bias.');
  } else if (test.bias === 'Even') {
    lines.push('Decision: Balanced (Equal values).');
  } else {
    lines.push(`Decision: ${test.bias}-Lateralized`);
  }

  // 2. Expected Pattern
  lines.push(`Expected RLP: ${getRuleDocExpectedRlp(test.testKey)}`);

  // 3. L/R values and decisive delta
  const left = formatValue(test.leftVal);
  const right = formatValue(test.rightVal);
  const diff = (test.leftVal !== null && test.rightVal !== null) ? Math.abs(test.leftVal - test.rightVal) : null;
  lines.push(`Values: L=${left}, R=${right} ${diff !== null ? `(Delta: ${diff}°)` : ''}`);

  // 4. Threshold context
  if (test.nominal) {
    lines.push(`Target Threshold: ${test.nominal}°`);
  }

  if (test.isHypermobile) {
    lines.push('⚠️ Hypermobility Flag: Value exceeds target threshold.');
  }

  // 5. Biomech / Contributing note
  const biomech = getRuleDocBiomechNote(test.testKey);
  if (biomech) {
    lines.push(`Note: ${biomech}`);
  }

  return lines;
};

export type IntakeLateralizationState = 'left' | 'right' | 'balanced' | 'mixed' | 'none';
/** Matches SopIntakeGoalFrameState for intake card goal-vs-nominal framing (YAML-driven). */
export type IntakeGoalBandState = 'none' | 'limitation' | 'pass' | 'hypermobile';
export type IntakeLegendTone =
  | 'neutral'
  | 'left'
  | 'right'
  | 'balanced'
  | 'mixed'
  | 'goal'
  | 'warning'
  | 'info'
  | 'limitation';
export type IntakeCardType = 'numeric-bilateral' | 'grounding' | 'foot-presentation';

export interface IntakeLegendItem {
  key: string;
  label: string;
  description: string;
  tone: IntakeLegendTone;
}

export interface IntakeExplanationPayload {
  headline: string;
  decisionSummary: string;
  expectedPattern: string;
  biomechNote: string;
  valueSummary: string;
  legend: IntakeLegendItem[];
}

const getLegendForCardType = (
  cardType: IntakeCardType,
  options: { showHypermobileLegend: boolean },
): IntakeLegendItem[] => {
  if (cardType === 'grounding') {
    return [
      { key: 'grounding-left', label: 'Blue', description: 'Left value is higher (winner side highlighted).', tone: 'left' },
      { key: 'grounding-right', label: 'Red', description: 'Right value is higher (winner side highlighted).', tone: 'right' },
      { key: 'grounding-equal', label: 'Neutral', description: 'Values are equal, no winner color applied.', tone: 'neutral' },
    ];
  }

  if (cardType === 'foot-presentation') {
    return [
      { key: 'foot-red', label: 'Red', description: 'Right side dominates (supination or less-pronated stance).', tone: 'right' },
      { key: 'foot-blue', label: 'Blue', description: 'Left side dominates (supination or less-pronated stance).', tone: 'left' },
      { key: 'foot-orange', label: 'Orange', description: 'Both feet are pronated states.', tone: 'warning' },
    ];
  }

  const legend: IntakeLegendItem[] = [
    { key: 'card-red', label: 'Card Red', description: 'Right-lateralized card state (SoP spectrum).', tone: 'right' },
    { key: 'card-blue', label: 'Card Blue', description: 'Left-lateralized card state (SoP spectrum).', tone: 'left' },
    { key: 'card-violet', label: 'Card Violet', description: 'Mixed lateralization (both L and R spectrum columns).', tone: 'mixed' },
    { key: 'card-green', label: 'Card Green', description: 'Balanced / at-goal (functional occupancy).', tone: 'balanced' },
    {
      key: 'goal-green',
      label: 'Input Green',
      description:
        'Measured value is neither a limitation (below intakeLimitationMinShortfallDeg) nor hypermobile (above intakeHypermobileMinSurplusDeg).',
      tone: 'goal',
    },
    {
      key: 'goal-red',
      label: 'Input Red',
      description: 'Limitation vs nominal: at least intakeLimitationMinShortfallDeg below goal (evaluation-rules.yaml).',
      tone: 'limitation',
    },
    {
      key: 'goal-yellow',
      label: 'Input Yellow',
      description: 'Hypermobile vs nominal: measured more than intakeHypermobileMinSurplusDeg above goal (evaluation-rules.yaml).',
      tone: 'warning',
    },
  ];

  if (options.showHypermobileLegend) {
    legend.push({
      key: 'hyper-marker',
      label: '^ Marker',
      description: 'Hypermobility indicator appears next to yellow-framed values.',
      tone: 'info',
    });
  }

  return legend;
};

export const buildIntakeExplanationPayload = (params: {
  testKey: string;
  testLabel: string;
  cardType: IntakeCardType;
  leftValue: number | null;
  rightValue: number | null;
  nominalValue?: number | null;
  lateralizationState: IntakeLateralizationState;
  leftGoalBand?: IntakeGoalBandState;
  rightGoalBand?: IntakeGoalBandState;
}): IntakeExplanationPayload => {
  const {
    testKey,
    testLabel,
    cardType,
    leftValue,
    rightValue,
    nominalValue = null,
    lateralizationState,
    leftGoalBand = 'none',
    rightGoalBand = 'none',
  } = params;

  const hasHypermobileState = leftGoalBand === 'hypermobile' || rightGoalBand === 'hypermobile';
  const expectedPattern = getRuleDocExpectedRlp(testKey);
  const biomechNote = getRuleDocBiomechNote(testKey);
  const valueSummary = `${testLabel}: L=${formatValue(leftValue)}, R=${formatValue(rightValue)}${
    nominalValue !== null ? `, goal=${nominalValue}°` : ''
  }.`;

  let decisionSummary = 'Insufficient values to classify this test.';
  if (lateralizationState === 'left') decisionSummary = 'Current read: left-lateralized (matches SoP Table simple columns).';
  if (lateralizationState === 'right') decisionSummary = 'Current read: right-lateralized (matches SoP Table simple columns).';
  if (lateralizationState === 'balanced') {
    decisionSummary = 'Current read: balanced / functional occupancy (matches SoP Table).';
  }
  if (lateralizationState === 'mixed') {
    decisionSummary = 'Current read: mixed lateralization (SoP Table highlights both L and R columns).';
  }

  const legend = getLegendForCardType(cardType, { showHypermobileLegend: hasHypermobileState });

  return {
    headline: `${testLabel} interpretation`,
    decisionSummary,
    expectedPattern,
    biomechNote,
    valueSummary,
    legend,
  };
};
